import { useCallback, useEffect, useState } from 'react';
import type { OperacionesClienteFinal } from '../api/ops/clienteFinal';
import type { EnlaceDeFirma } from '../api/ops/documentosCliente';
import { ErrorApi } from '../api/problema';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { navegador } from '../logica/navegador';
import { RUTAS_CLIENTE } from '../rutas';
import { BotonPc, Pagina } from './Pagina';
import { direccionDeVuelta, plazo } from './firma';
import './firma.css';

/**
 * Firmar un documento por el enlace del mensaje (9.12): `/firma/{negocio}/{token}`, anónima, con `getSignatureLink` y
 * `startSigningSessionByLink`. **Marca de Reservaria con el nombre del negocio** (G798, ADR-0206): cabecera con la inicial
 * y el nombre del negocio, pie «Ofrecido por Reservaria», sin el aspecto ni los colores de su web; la pestaña dice
 * «Firma tu documento para {negocio}» (G477).
 *
 * El documento para leer («Ver el documento entero»), «Firmar» abre la firma del proveedor eIDAS (G56, sin nombrarlo) y
 * vuelve aquí con `?vuelta=1`: «Comprobando tu firma» mientras el enlace siga pendiente unos segundos; si sigue pendiente,
 * «No has terminado de firmar» con «Volver a intentarlo». El proveedor caído (`502`) no firma nada. Un enlace que ya no
 * vale (`404`: caducado, anulado o de otro negocio) lleva a «Mis documentos» (con `?negocio=`, G854). Un enlace ya usado
 * responde `200` con `status = signed` (G852): «Este documento ya está firmado». Se sirve en `PANEL_URL/firma` (G851).
 */
interface Props {
    ops: OperacionesClienteFinal;
    negocio: string;
    token: string;
    /** Vuelve del proveedor de firma (`?vuelta=1`). */
    vuelta: boolean;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    /** Espera entre comprobaciones a la vuelta (las pruebas la acortan). */
    espera?: number;
}

type Estado =
    | { tipo: 'cargando' }
    | { tipo: 'error' }
    | { tipo: 'caducado' }
    | { tipo: 'comprobando'; doc: EnlaceDeFirma | null }
    | { tipo: 'listo'; doc: EnlaceDeFirma; aviso: 'volver' | 'proveedor' | null }
    | { tipo: 'firmado'; doc: EnlaceDeFirma };

const INTENTOS_A_LA_VUELTA = 4;

export function PaginaFirma({ ops, negocio, token, vuelta, navegar, espera = 2000 }: Props) {
    const [estado, setEstado] = useState<Estado>(vuelta ? { tipo: 'comprobando', doc: null } : { tipo: 'cargando' });
    const [intento, setIntento] = useState(0);
    const [entero, setEntero] = useState(false);
    const [abriendo, setAbriendo] = useState(false);
    const nombre = estado.tipo === 'listo' || estado.tipo === 'firmado' || (estado.tipo === 'comprobando' && estado.doc !== null) ? (estado.doc?.business_name ?? '') : '';

    useEffect(() => {
        if (nombre !== '') {
            document.title = `Firma tu documento para ${nombre}`;
        }
    }, [nombre]);

    useEffect(() => {
        let vivo = true;
        let temporizador: ReturnType<typeof setTimeout> | undefined;
        const leer = (n: number) => {
            ops.getSignatureLink(negocio, token)
                .then((doc) => {
                    if (!vivo) {
                        return;
                    }
                    if (doc.status === 'signed') {
                        setEstado({ tipo: 'firmado', doc });
                    } else if (doc.status !== 'requested') {
                        setEstado({ tipo: 'caducado' });
                    } else if (vuelta && intento === 0 && n < INTENTOS_A_LA_VUELTA) {
                        // A la vuelta del proveedor, el aviso de la firma puede tardar unos segundos.
                        setEstado({ tipo: 'comprobando', doc });
                        temporizador = setTimeout(() => { leer(n + 1); }, espera);
                    } else {
                        setEstado({ tipo: 'listo', doc, aviso: vuelta && intento === 0 ? 'volver' : null });
                    }
                })
                .catch((e: unknown) => {
                    if (vivo) {
                        setEstado(e instanceof ErrorApi && e.estado === 404 ? { tipo: 'caducado' } : { tipo: 'error' });
                    }
                });
        };
        leer(0);
        return () => {
            vivo = false;
            clearTimeout(temporizador);
        };
    }, [ops, negocio, token, vuelta, intento, espera]);

    const firmar = useCallback(
        (doc: EnlaceDeFirma) => {
            setAbriendo(true);
            ops.startSigningSessionByLink(negocio, token, direccionDeVuelta(window.location.href))
                .then((s) => {
                    navegador.ir(s.url);
                })
                .catch((e: unknown) => {
                    setAbriendo(false);
                    if (e instanceof ErrorApi && e.estado === 404) {
                        setEstado({ tipo: 'caducado' });
                    } else {
                        setEstado({ tipo: 'listo', doc, aviso: 'proveedor' });
                    }
                });
        },
        [ops, negocio, token],
    );

    const cabecera = nombre === '' ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, linea: 'Firmar un documento' } as const);
    const misDocumentos = () => { navegar(`${RUTAS_CLIENTE.misDocumentos}?negocio=${encodeURIComponent(negocio)}`); };

    if (estado.tipo === 'cargando') {
        return (
            <Pagina cabecera={cabecera} pie={{ tipo: 'reservaria' }}>
                <section className="rv-pc-card" aria-busy="true" aria-label="Cargando el documento">
                    <div className="rv-pc-esqueleto" style={{ height: 24, width: '50%' }} />
                    <div className="rv-pc-esqueleto" style={{ height: 32, width: '80%' }} />
                    <div className="rv-pc-esqueleto" style={{ height: 240 }} />
                </section>
            </Pagina>
        );
    }
    if (estado.tipo === 'error') {
        return (
            <Pagina cabecera={cabecera} pie={{ tipo: 'reservaria' }} centrada>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>No hemos podido cargar el documento</h1><span>Comprueba la conexión y prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setEstado({ tipo: 'cargando' }); setIntento((i) => i + 1); }} />
                </section>
            </Pagina>
        );
    }
    if (estado.tipo === 'caducado') {
        return (
            <Resultado cabecera={cabecera} icono="link" fondo="var(--rv-paper)" tinta="var(--rv-ink)" chip="Enlace no válido" chipEstado="problem" titulo="Este enlace de firma ya no funciona"
                texto="Puede que haya pasado el plazo para firmar desde el móvil, que la cita se haya cancelado o que ya lo firmaras. Si tu cita sigue en pie, lo firmas al llegar al centro.">
                <BotonPc texto="Ver mis documentos" icono="file-text" onClick={misDocumentos} />
            </Resultado>
        );
    }
    if (estado.tipo === 'comprobando') {
        return (
            <Resultado cabecera={cabecera} icono="loader-circle" gira fondo="var(--rv-paper)" tinta="var(--rv-ink)" chip="Comprobando" chipEstado="pending" titulo="Comprobando tu firma"
                texto="El servicio de firma nos está confirmando que has firmado. Tarda unos segundos; no cierres esta página." />
        );
    }
    if (estado.tipo === 'firmado') {
        return (
            <Resultado cabecera={cabecera} icono="check" fondo="var(--rv-ink)" tinta="#fff" chip="Firmado" chipEstado="confirmed" titulo="Este documento ya está firmado"
                texto="No tienes que hacer nada más. Si quieres una copia, la tienes en «Mis documentos».">
                <BotonPc texto="Ver mis documentos" icono="file-text" onClick={misDocumentos} />
                <p className="rv-pc-nota"><Icono nombre="shield-check" /><span>Firma electrónica avanzada (eIDAS). Guardamos el documento y sus pruebas el tiempo que obliga la ley.</span></p>
            </Resultado>
        );
    }

    const { doc, aviso } = estado;
    const parrafos = doc.rendered.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p !== '');
    return (
        <Pagina cabecera={cabecera} pie={{ tipo: 'reservaria' }}>
            <section className="rv-pc-card" aria-labelledby="rv-fd-t">
                {aviso !== null && (
                    <div role="alert" className={`rv-fd-vuelta${aviso === 'proveedor' ? ' rv-fd-vuelta--error' : ''}`}>
                        <Icono nombre={aviso === 'proveedor' ? 'circle-alert' : 'clock'} />
                        <span>
                            <strong>{aviso === 'proveedor' ? 'El servicio de firma no responde' : 'No has terminado de firmar'}</strong>
                            <span>{aviso === 'proveedor' ? 'No se ha firmado nada. Prueba otra vez en unos minutos.' : `No se ha firmado nada. Puedes volver a intentarlo hasta el ${plazo(doc.sign_by)}.`}</span>
                        </span>
                    </div>
                )}
                <div className="rv-pc-baldosa-fila">
                    <span aria-hidden="true" className="rv-pc-baldosa" style={{ background: 'var(--rv-ink)', color: '#fff' }}><Icono nombre="file-text" /></span>
                    <Chip estado="pending">Pendiente de firmar</Chip>
                </div>
                <div className="rv-pc-card__cab">
                    <span className="rv-fd-ceja">{`Para firmar · ${doc.business_name}`}</span>
                    <h1 id="rv-fd-t">{doc.title}</h1>
                </div>
                <p className="rv-fd-plazo"><Icono nombre="clock" /><span>{`Fírmalo antes del ${plazo(doc.sign_by)}`}</span></p>
                <div className={`rv-fd-doc${entero ? ' rv-fd-doc--entero' : ''}`}>
                    {parrafos.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <button type="button" className="rv-pc-l" aria-expanded={entero} onClick={() => { setEntero((x) => !x); }}>
                    <Icono nombre={entero ? 'chevron-up' : 'chevron-down'} />
                    <span>{entero ? 'Ver menos' : 'Ver el documento entero'}</span>
                </button>
                {doc.identity_check === true && (
                    <div className="rv-pc-capa">
                        <Icono nombre="fingerprint" />
                        <span><strong>Comprobaremos que eres tú</strong><br />Al firmar, el servicio de firma te mandará un código al móvil. Es una firma electrónica con validez legal (eIDAS).</span>
                    </div>
                )}
                <BotonPc texto={aviso === null ? 'Firmar' : 'Volver a intentarlo'} icono="pen-line" ocupado={abriendo} ocupadoTexto="Abriendo…" onClick={() => { firmar(doc); }} />
            </section>
        </Pagina>
    );
}

function Resultado({ cabecera, icono, gira = false, fondo, tinta, chip, chipEstado, titulo, texto, children }: {
    cabecera: { tipo: 'cuenta' } | { tipo: 'negocio'; nombre: string; linea: string };
    icono: NombreIcono;
    gira?: boolean;
    fondo: string;
    tinta: string;
    chip: string;
    chipEstado: EstadoChip;
    titulo: string;
    texto: string;
    children?: React.ReactNode;
}) {
    return (
        <Pagina cabecera={cabecera} pie={{ tipo: 'reservaria' }} centrada>
            <section className="rv-pc-card" aria-labelledby="rv-fd-r">
                <div className="rv-pc-baldosa-fila">
                    <span aria-hidden="true" className="rv-pc-baldosa" style={{ background: fondo, color: tinta }}><Icono nombre={icono} className={gira ? 'rv-gira' : undefined} /></span>
                    <Chip estado={chipEstado}>{chip}</Chip>
                </div>
                <div className="rv-pc-card__cab"><h1 id="rv-fd-r">{titulo}</h1><span>{texto}</span></div>
                {children}
            </section>
        </Pagina>
    );
}
